import React, { useCallback, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useApiCaller } from '../../utils/ApiUtilities';
import {
    getSuperAdminAdminsUrl,
    getSuperAdminMarkRevisionUrl,
    getSuperAdminQuestionsUrl,
} from '../../utils/UrlUtilities';
import { buildMarkRevisionPayload } from '../../utils/PayloadUtils';
import { formatBdPst } from '../../utils/DateUtilities';
import QuestionEditForm from '../QuestionEditForm';
import QuestionAuditDrawer from './QuestionAuditDrawer';
import CloseButton from '../ui/CloseButton';

function fmtDate(d) {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
}

function ReviewBadge({ q }) {
    if (q.needs_revision) {
        return <span className="badge bg-warning text-dark">Needs Revision</span>;
    }
    if (q.super_admin_reviewed) {
        const by = q.super_admin_reviewed_by?.name || 'Super Admin';
        const at = q.super_admin_reviewed_at
            ? formatBdPst(q.super_admin_reviewed_at, { withTime: false })
            : '';
        return (
            <span className="badge bg-success" title={`${by} on ${at}`}>
                ✓ Reviewed
            </span>
        );
    }
    if (q.revision_resolved) {
        return (
            <span
                className="badge bg-info text-dark"
                title="Admin addressed the revision note — awaiting super-admin re-review"
            >
                Resolved
            </span>
        );
    }
    return <span className="badge bg-secondary">Pending</span>;
}

function Modal({ title, children, onClose, wide = false }) {
    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ background: 'rgba(0,0,0,0.4)' }}
            onClick={onClose}
        >
            <div
                className={`modal-dialog ${wide ? 'modal-xl' : ''}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">{title}</h5>
                        <CloseButton onClick={onClose} />
                    </div>
                    <div className="modal-body">{children}</div>
                </div>
            </div>
        </div>
    );
}

function MarkRevisionForm({ question, onCancel, onSaved }) {
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const CallApi = useApiCaller();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!note.trim()) return;
        setSubmitting(true);
        const res = await CallApi({
            url: getSuperAdminMarkRevisionUrl(question.question_id),
            method: 'POST',
            payload: buildMarkRevisionPayload(note.trim()),
        });
        setSubmitting(false);
        if (res.success) onSaved();
    };

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-3">
                <label>Note to admin <span className="text-danger">*</span></label>
                <textarea
                    className="form-control"
                    rows={5}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Describe what needs to change…"
                    required
                />
            </div>
            <div className="d-flex gap-2">
                <button type="submit" className="btn btn-warning" disabled={!note.trim() || submitting}>
                    {submitting ? 'Sending…' : 'Send Back for Revision'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
            </div>
        </form>
    );
}

export default function QuestionAuditGrid() {
    const [admins, setAdmins] = useState([]);
    const [adminId, setAdminId] = useState('');
    const [createdFrom, setCreatedFrom] = useState(null);
    const [createdTo, setCreatedTo] = useState(null);
    const [reviewedFilter, setReviewedFilter] = useState(''); // '', 'true', 'false'
    const [needsRevisionFilter, setNeedsRevisionFilter] = useState(''); // '', 'true', 'false'
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [total, setTotal] = useState(0);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null);
    const [marking, setMarking] = useState(null);
    const [auditFor, setAuditFor] = useState(null);
    const CallApi = useApiCaller();

    const reload = useCallback(async () => {
        setLoading(true);
        const url = getSuperAdminQuestionsUrl({
            page,
            limit,
            adminId: adminId || undefined,
            createdFrom: createdFrom ? fmtDate(createdFrom) : undefined,
            createdTo: createdTo ? fmtDate(new Date(createdTo.getTime() + 86400000)) : undefined,
            superAdminReviewed: reviewedFilter,
            needsRevision: needsRevisionFilter,
            search: search.trim() || undefined,
        });
        const res = await CallApi({ url, method: 'GET' });
        if (res.success) {
            setQuestions(res.data.questions || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [CallApi, page, limit, adminId, createdFrom, createdTo, reviewedFilter, needsRevisionFilter, search]);

    useEffect(() => {
        (async () => {
            const res = await CallApi({ url: getSuperAdminAdminsUrl(), method: 'GET' });
            if (res.success) setAdmins(res.data || []);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const totalPages = Math.max(1, Math.ceil(total / limit));

    return (
        <div>
            <h4 className="fw-bold mb-3" style={{ color: '#4c1d95' }}>Questions Audit</h4>
            <div className="row g-2 mb-3 align-items-end kp-filter">
                <div className="col-md-3">
                    <label className="form-label small">Admin</label>
                    <select
                        className="form-select"
                        value={adminId}
                        onChange={(e) => { setAdminId(e.target.value); setPage(1); }}
                    >
                        <option value="">All admins</option>
                        {admins.map(a => (
                            <option key={a.id} value={a.id}>{a.name} ({a.email})</option>
                        ))}
                    </select>
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Created from</label>
                    <DatePicker
                        selected={createdFrom}
                        onChange={(d) => { setCreatedFrom(d); setPage(1); }}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        isClearable
                        placeholderText="From"
                    />
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Created to</label>
                    <DatePicker
                        selected={createdTo}
                        onChange={(d) => { setCreatedTo(d); setPage(1); }}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        isClearable
                        placeholderText="To"
                    />
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Reviewed</label>
                    <select
                        className="form-select"
                        value={reviewedFilter}
                        onChange={(e) => { setReviewedFilter(e.target.value); setPage(1); }}
                    >
                        <option value="">All</option>
                        <option value="true">Reviewed</option>
                        <option value="false">Not reviewed</option>
                    </select>
                </div>
                <div className="col-md-3">
                    <label className="form-label small">Search</label>
                    <input
                        className="form-control"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); reload(); } }}
                        placeholder="text / topic / subject"
                    />
                </div>
                <div className="col-md-2">
                    <label className="form-label small">Needs Revision</label>
                    <select
                        className="form-select"
                        value={needsRevisionFilter}
                        onChange={(e) => { setNeedsRevisionFilter(e.target.value); setPage(1); }}
                    >
                        <option value="">All</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </div>
            </div>

            {loading ? <div>Loading…</div> : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle">
                        <thead className="table-light">
                            <tr>
                                <th>ID</th>
                                <th style={{ minWidth: 240 }}>Question</th>
                                <th>Subject</th>
                                <th>Created by</th>
                                <th>Created at</th>
                                <th>Updated by</th>
                                <th>Updated at</th>
                                <th>Review</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {questions.length === 0 && (
                                <tr><td colSpan={9} className="text-center text-muted">No questions</td></tr>
                            )}
                            {questions.map((q) => (
                                <tr key={q.question_id}>
                                    <td>{q.question_id}</td>
                                    <td>
                                        <div style={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>
                                            {q.question_text}
                                        </div>
                                    </td>
                                    <td>{q.subject}</td>
                                    <td>{q.created_by?.name || <span className="text-muted">—</span>}</td>
                                    <td>{q.created_at ? formatBdPst(q.created_at) : '—'}</td>
                                    <td>{q.updated_by?.name || <span className="text-muted">—</span>}</td>
                                    <td>{q.updated_at ? formatBdPst(q.updated_at) : '—'}</td>
                                    <td><ReviewBadge q={q} /></td>
                                    <td>
                                        <div className="btn-group btn-group-sm" role="group">
                                            <button className="btn btn-outline-primary" onClick={() => setEditing(q)}>
                                                Edit
                                            </button>
                                            <button className="btn btn-outline-warning" onClick={() => setMarking(q)}>
                                                Mark for Revision
                                            </button>
                                            <button className="btn btn-outline-secondary" onClick={() => setAuditFor(q.question_id)}>
                                                History
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">Total: {total}</small>
                <div className="btn-group">
                    <button
                        className="btn btn-outline-primary btn-sm"
                        disabled={page <= 1}
                        onClick={() => setPage(p => p - 1)}
                    >
                        ← Prev
                    </button>
                    <span className="btn btn-sm disabled">Page {page} / {totalPages}</span>
                    <button
                        className="btn btn-outline-primary btn-sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next →
                    </button>
                </div>
            </div>

            {editing && (
                <Modal title={`Edit Question #${editing.question_id}`} onClose={() => setEditing(null)} wide>
                    <QuestionEditForm
                        question={editing}
                        onCancel={() => setEditing(null)}
                        onSave={() => { setEditing(null); reload(); }}
                    />
                </Modal>
            )}

            {marking && (
                <Modal title={`Send back for revision — #${marking.question_id}`} onClose={() => setMarking(null)}>
                    <MarkRevisionForm
                        question={marking}
                        onCancel={() => setMarking(null)}
                        onSaved={() => { setMarking(null); reload(); }}
                    />
                </Modal>
            )}

            {auditFor && (
                <QuestionAuditDrawer
                    questionId={auditFor}
                    onClose={() => setAuditFor(null)}
                />
            )}
        </div>
    );
}
